// Ports the Kiosk-style sales mode filtering into POS (filters by "POS" support instead of "KIOSK")
// Keeps the same output shape so downstream Kiosk-style components can read it from the store.

type ModeOfSale = { designation: string; state: boolean };

export type SalesModeApiItem = {
    "Mode of sale": string;
    support_vente: string[];
};

function buildSaleSupports(
    responseData: SalesModeApiItem[]
): { saleSupports: ModeOfSale[] }[] {
    return responseData.map((saleMode) => ({
        saleSupports: saleMode.support_vente
            .map<ModeOfSale | undefined>((el) => {
                if (el === "POS")
                    return {
                        designation: saleMode["Mode of sale"],
                        state: false,
                    };
                return undefined;
            })
            .filter((x): x is ModeOfSale => x !== undefined),
    }));
}

function mergeWithExisting(
    saleModes: { saleSupports: ModeOfSale[] }[],
    existingList: ModeOfSale[] | undefined
): ModeOfSale[] {
    const rowDataSales: ModeOfSale[] = existingList ? [...existingList] : [];

    // Remove row data entries that are not in saleModes supports (stale)
    const supportDesignations = new Set<string>();
    saleModes.forEach((el) =>
        el.saleSupports.forEach((elt) => {
            supportDesignations.add(elt.designation);
        })
    );
    const filteredRowData = rowDataSales.filter((o) =>
        supportDesignations.has(o.designation)
    );

    // Merge: take existing row data first, then add missing supports
    let result: ModeOfSale[] = [];
    const includedDesignations = new Set<string>();

    if (filteredRowData.length > 0) {
        filteredRowData.forEach((el) => {
            result.push(el);
            includedDesignations.add(el.designation);
        });
    }

    saleModes.forEach((el) => {
        el.saleSupports.forEach((elt) => {
            if (!includedDesignations.has(elt.designation)) {
                result.push(elt);
                includedDesignations.add(elt.designation);
            }
        });
    });

    return result;
}

export function buildSalesModes(responseData: SalesModeApiItem[], rowData: any) {
    const saleModes = buildSaleSupports(responseData);

    const customerTicket = mergeWithExisting(
        saleModes,
        rowData?.["customer_ticket"]?.dateTimeDeliveryModeSale
    );

    const kitchenTicket = mergeWithExisting(
        saleModes,
        rowData?.["Kitchen_ticket"]?.ticketKitchenModeSale
    );

    const salesCustomerAddressModeSale = mergeWithExisting(
        saleModes,
        rowData?.["Kitchen_ticket"]?.customerAddressModeSale
    );

    const customerTicketSalesModes = mergeWithExisting(
        saleModes,
        rowData?.["customer_ticket"]?.customerTicketSalesModes
    );

    const customerInformationName = mergeWithExisting(
        saleModes,
        rowData?.["Kitchen_ticket"]?.customer_information_mode_sale
            ?.customerInformationModeSaleName
    );

    const customerInformationNumTel = mergeWithExisting(
        saleModes,
        rowData?.["Kitchen_ticket"]?.customer_information_mode_sale
            ?.customerInformationModeSaleNumTel
    );

    const customerInformationAddress = mergeWithExisting(
        saleModes,
        rowData?.["Kitchen_ticket"]?.customer_information_mode_sale
            ?.customerInformationModeSaleAdress
    );

    const customerInformationCity = mergeWithExisting(
        saleModes,
        rowData?.["Kitchen_ticket"]?.customer_information_mode_sale
            ?.customerInformationModeSaleCity
    );

    const customerInformationCompany = mergeWithExisting(
        saleModes,
        rowData?.["Kitchen_ticket"]?.customer_information_mode_sale
            ?.customerInformationModeSaleCompany
    );

    const customerInformationCode = mergeWithExisting(
        saleModes,
        rowData?.["Kitchen_ticket"]?.customer_information_mode_sale
            ?.customerInformationModeSaleCode
    );

    return {
        customerTicketSalesModes,
        customerInformationCompany,
        customerInformationCity,
        customerInformationName,
        customerInformationNumTel,
        customerInformationAddress,
        customerInformationCode,
        salesCustomerAddressModeSale,
        customerTicket,
        kitchenTicket,
    };
}
